import { useCallback, useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	CENTRAL_TEAM_DASHBOARD,
	INTERVIEW_LANDING,
} from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { UndoOutlined } from '@ant-design/icons';
import { useApplicationsList } from '../ApplicationsList/useApplicationsList';
import { FilterCTDApplications } from './FilterCTDApplications';
import { CTDApplicationsList } from './CTDApplicationsList';
import { ApplicationSummary } from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { ApplicationStatus, FROM_CTD } from '../../../shared/Constants';
import { getAdminCentralTeamFilters } from '../../../shared/Utils';
import useQueryParams from '../../../hooks/useQueryParams';
import { useLocation } from 'react-router-dom';
import { CentralTeamDashboardFilter } from '../../../shared/ScreeningServiceTypes';

const { Text } = Typography;

export const CentralTeamApplicationsList = () => {
	const { t, i18n } = useTranslation();
	const { navigateWithBatchId } = useBatchId();
	const { navigateWithApplicationId } = useApplicationId();
	const lengthyLanguages = ['ta', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const queryParams = useQueryParams();
	const categoryKey = queryParams.get('category');
	const location = useLocation();
	const initialCTDFilter = (
		location.state as {
			CTDFilter: CentralTeamDashboardFilter;
		}
	)?.CTDFilter;
	const [ctdFilter, setCTDFilter] = useState<
		CentralTeamDashboardFilter | undefined
	>(initialCTDFilter);
	const { applicationsList, onPageChange, filter, onFilterChange, paging } =
		useApplicationsList({ categoryKey, CTDFilter: ctdFilter });

	useEffect(() => {
		if (ctdFilter?.regionId || ctdFilter?.zoneId) {
			setIsFilterApplied(true);
		}
	}, [ctdFilter]);

	useEffect(() => {
		if (
			filter &&
			(filter.regionIds ||
				filter.zoneId?.length ||
				filter.searchByApplicantName ||
				filter.applicantSrcmId)
		) {
			setIsFilterApplied(true);
		}
	}, [filter]);

	const handleBackClick = useCallback(() => {
		onFilterChange({ status: [ApplicationStatus.FUNCTIONARY_ASSIGNED] });
		navigateWithBatchId(CENTRAL_TEAM_DASHBOARD);
	}, [navigateWithBatchId, onFilterChange]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		const applicationsFilter = getAdminCentralTeamFilters(categoryKey);
		if (applicationsFilter) {
			onFilterChange(applicationsFilter);
			setCTDFilter(undefined);
			setIsFilterApplied(false);
		}
	}, [categoryKey, onFilterChange]);

	const handleEvaluateClick = useCallback(
		(application: ApplicationSummary) => {
			sessionStorage.setItem('interviewOrigin', FROM_CTD);
			navigateWithApplicationId(INTERVIEW_LANDING, application.applicationId);
		},
		[navigateWithApplicationId]
	);

	return (
		<>
			<div className="md:hidden flex justify-center items-center pt-3 pb-1 relative">
				<Text className="font-sans font-bold text-lg mb-1 text-center">
					{categoryKey && t(categoryKey)}
				</Text>
			</div>
			<div className="flex justify-between items-center pt-1 pb-3 px-5 md:px-5 md:py-5 relative">
				{/* Back Button - Desktop */}
				<Button
					type="primary"
					onClick={handleBackClick}
					className="hidden md:block font-bold text-sm"
				>
					<span className="flex gap-2">
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</span>
				</Button>

				{/* Back Button + Title - Mobile */}
				<div className="block sm:hidden">
					<a
						onClick={handleBackClick}
						className="relative top:0 md:top-[3px] mr-2 font-bold text-sm"
					>
						<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
					</a>
				</div>

				{/* Centered Title */}
				<Text className="hidden md:block font-sans font-bold text-xl text-center mb-1 absolute left-1/2 transform -translate-x-1/2">
					{categoryKey && t(categoryKey)}
				</Text>

				{/* Filter Button - Desktop */}
				<div
					className={`flex ${
						isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
					}`}
				>
					{isFilterApplied ? (
						<Button
							className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={handleResetFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
					<Button
						className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterApplied ? t('filterApplied') : t('filter')}
					</Button>
				</div>

				{/* Filter Icon - Mobile */}
				<Button
					className="block sm:hidden"
					variant="outlined"
					onClick={handleFilterPopup}
				>
					<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
				</Button>
			</div>
			<FilterCTDApplications
				open={isFilterModalOpen}
				onClose={handleFilterModalClose}
				filterValue={filter}
				onFilterChange={onFilterChange}
				CTDFilter={ctdFilter}
				onResetFilters={handleResetFilters}
				resetFromList={isFilterApplied}
			/>
			<CTDApplicationsList
				data={applicationsList}
				onPageChange={onPageChange}
				onEvaluateClick={handleEvaluateClick}
				pageConfig={paging}
			/>
		</>
	);
};
